import { config, apiUrl, infoApiUrl} from "../../constants";

const safeGetItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : undefined;
  } catch (e) {
    console.error(`Error parsing localStorage key "${key}":`, e);
    return undefined;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Could not save to localStorage key "${key}" (likely quota exceeded):`, e);
  }
};

export const entityApi = {
  /**
   * Logs a user in, returning a promise with `true` when done
   */
  getAll(language = "es") {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ language })
    };

    //  console.log("[entityApi] getAll called with language:", language);
    return fetch(`${apiUrl}/Utils/getAyuntamientos`, requestOptions)
      .then(response => response.json())
      .then(entities => {
        //console.log("[entityApi] getAll success, entities count:", entities ? entities.length : 0);
        safeSetItem("entities", entities);
        return entities;
      });
  },
  /**
   * Registers a user and then logs them in
   * @param  {string} id The ayuntamientoID
   */
  getEntityById(id, language = "es") {
    let ayuntamientoID = id;
    if (id == 184 || id == 3) {
      ayuntamientoID = 3;
    } 

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ language, ayuntamientoID })
    };

    return fetch(`${apiUrl}/Utils/getAyuntamientoByAytoID`, requestOptions)
      .then(response => response.json())
      .then(entity => {
        safeSetItem("entity", entity);
        return entity;
      });
  },
  /**
   * Registers a user and then logs them in
   * @param  {string} address The street address
   */
  getMoreStreetsFromExternalUrl(address) {
    const requestOptions = {
      method: "GET"
      // headers: { 'Content-Type': 'application/json' }
    };

    return fetch(`https://sig.malaga.eu/territorio/direcciones/geojson/?direccion=${address}&epsg=4258`, requestOptions)
      .then(response => response.json())
      .then(resp => {
        return resp;
      });
  },
  /**
   * Registers a user and then logs them in
   * @param  {string} token The ayuntamientoID
   */
  getTipologiaPorAyuntamiento(token, idioma = localStorage.getItem('i18nextLng')) {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, idioma })
    };

    return fetch(
      `${apiUrl}/Typology/getTipologiaPorAyuntamiento`,
      requestOptions
    )
      .then(response => response.json())
      .then(typology => {
        const { Elementos } = typology;
        const uniqueElementosSet = Elementos?.filter(
          (ele, ind) =>
            ind ===
            Elementos.findIndex(
              elem => elem.TipoElementoID === ele.TipoElementoID
            )
        );
        typology.Elementos = uniqueElementosSet;
        safeSetItem("typology", typology);
        return typology;
      });
  },
  getCallesPorAyuntamiento(token) {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    };

    return fetch(`${apiUrl}/Utils/getCallesPorAyuntamiento`, requestOptions)
      .then(response => response.json())
      .then(streets => {
        safeSetItem("streets", streets);
        return streets;
      });
  },
  getUnidadesPorAyuntamiento(token) {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    };

    return fetch(`${apiUrl}/Emvisesa/GetUnidad`, requestOptions)
      .then(response => response.json())
      .then(units => {
        if (!units || (units && !Array.isArray(units))) {
          units = [];
        }
        safeSetItem("unitsTypes", units);
        return units;
      });
  },
  getPromocionesPorAyuntamiento(token) {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    };

    return fetch(`${apiUrl}/Emvisesa/GetPromocion`, requestOptions)
      .then(response => response.json())
      .then(promotions => {
        if (!promotions || (promotions && !Array.isArray(promotions))) {
          promotions = [];
        }
        safeSetItem("promotionsTypes", promotions);
        return promotions;
      });
  },
  getEdificiosPorAyuntamiento(token) {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    };

    return fetch(`${apiUrl}/Utils/getEdificiosPorAyuntamiento`, requestOptions)
      .then(response => response.json())
      .then(buildings => {
        safeSetItem("buildings", buildings);
        return buildings;
      });
  },
  getMoreData(AyuntamientoID = undefined, user = localStorage.user) {
    if (!AyuntamientoID) {
      //console.error("entityApi getMoreData", AyuntamientoID);
      return Promise.reject("AyuntamientoID no definido");
    }

    const requestOptions = { method: "GET" };

    const fetchConfig = (id) => {
      let configId = id;
      if (id == 184 || id == 3) {
        configId = 3;
      } 

      const baseUrl = config.apiUrl;
      const url = `${baseUrl}/Utils/getAppConfig/${configId}`;

      return fetch(url, requestOptions)
        .then(response => {
          if (!response.ok) {
            // Si no existe el JSON para este AO
            if (id !== 50) {
              console.warn(`AO ${id} no encontrado, cargando AO 50 por defecto`);
              return fetchConfig(50); // Reintenta con 50
            }
            throw new Error(`AO ${id} no encontrado`);
          }
          return response.json();
        });
    };

    return fetchConfig(AyuntamientoID)
      .then(config => {
        safeSetItem("entityConfig", config);
        return config;
      })
      .catch(err => {
        console.error("Error al cargar config:", err);
        throw err;
      });
  },
  getEntityInfo(id) {
    const requestOptions = {
      method: "GET"
      // headers: { 'Content-Type': 'application/json' }
    };

    return fetch(`${infoApiUrl}/Data/${id}`, requestOptions)
      .then(response => response.json())
      .then(entityInfo => {
        safeSetItem("entityInfo", entityInfo);
        return entityInfo;
      });
  },
  loadEntity() {
    return safeGetItem("entity");
  },
  loadEntityInfo() {
    return safeGetItem("entityInfo");
  },
  loadEntityConfig() {
    return safeGetItem("entityConfig");
  },
  loadEntityStreets() {
    return safeGetItem("streets");
  },
  loadEntityBuildings() {
    return safeGetItem("buildings");
  },
  loadEntities() {
    const entities = safeGetItem("entities");
    return Array.isArray(entities) ? entities : undefined;
  },
  changeEntity() {
    [
      "entity",
      "typology",
      "streets",
      "buildings",
      "entityConfig",
      "unitsTypes",
      "promotionsTypes",
      "entityInfo"
    ].forEach(key => localStorage.removeItem(key));

    return Promise.resolve(true);
  },
  onChange() { }
};

export default entityApi;
